import { Dropdown, DropdownWidth, Input, SegmentedControl, typographyRem } from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import utc from 'dayjs/plugin/utc'
import React, { useContext, useEffect, useState } from 'react'

import { PaymentType, PremiumPaymentFrequency } from '../../../../generated/graphql'
import { useQuote } from '../../../../hooks/useQuote'
import { CoverActionsProps } from '../../../../pages/Cover/Cover'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { daysWithinNextMonth } from '../../../../utils/daysWithinNextMonth'
import { ToolTip } from '../../../ToolTip'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { handleDropdownState } from '../../utils/handleDropdownState'
import { getPossibleExcessOptions } from './utils/excessOptions'
import { excessModalText, policyStartModalText, renewalQuoteModalText } from './utils/modalText'
const { h5: H5, paragraphBody: ParagraphBody } = typographyRem
dayjs.extend(utc)
dayjs.extend(isToday)

interface CoverYourInsuranceProps {
  coverActions: CoverActionsProps
  setCoverActions: (value: CoverActionsProps) => void
}

export const CoverYourInsurance = ({ coverActions, setCoverActions }: CoverYourInsuranceProps) => {
  const quote = useQuote()
  const {
    state: { isMobile },
  } = useContext(ProviderContext)

  const { showErrorBanner, notAnsweredMessage, updateValidInputs, validInputs } = usePreQuoteContext()

  const [policyStart, setPolicyStart] = useState<string | undefined | null>(
    coverActions?.setInceptionDate?.inceptionDate || quote?.details?.policy?.inception_date,
  )
  const [voluntaryExcess, setVoluntaryExcess] = useState<number | undefined | null>(
    coverActions?.setVoluntaryExcess?.voluntaryExcess || quote?.details?.policy?.excess,
  )
  const [renewalValue, setRenewalValue] = useState<number | undefined | null>(
    coverActions?.setCurrentRenewalPrice?.current || quote?.details?.policy?.current,
  )
  const [paymentFrequency, setPaymentFrequency] = useState<PaymentType | PremiumPaymentFrequency | null | undefined>(
    coverActions?.setPaymentType?.paymentType || quote?.details?.policy?.paymentType || null,
  )

  useEffect(() => {
    if (policyStart) {
      updateValidInputs({ field: 'policyStart', value: true })
    }
  }, [policyStart])

  useEffect(() => {
    if (typeof voluntaryExcess === 'number') {
      updateValidInputs({ field: 'excess', value: true })
    }
  }, [voluntaryExcess])

  useEffect(() => {
    if (paymentFrequency) {
      updateValidInputs({ field: 'paymentType', value: true })
    }
  }, [paymentFrequency])

  const handlePolicyStart = (inceptionDate: string) => {
    setPolicyStart(inceptionDate)
    setCoverActions({ ...coverActions, setInceptionDate: { inceptionDate } })
    DataLayer.customEvent({
      eventName: 'start date',
      section: 'cover',
      eventType: 'form tracking',
    })
  }

  const handlePolicyExcess = (excess: number) => {
    setVoluntaryExcess(excess)
    setCoverActions({ ...coverActions, setVoluntaryExcess: { voluntaryExcess: excess } })
    DataLayer.customEvent({
      eventName: 'voluntary excess',
      section: 'cover',
      eventType: 'form tracking',
    })
  }

  const handlePaymentFrequency = (paymentType: PaymentType) => {
    setPaymentFrequency(paymentType)
    setCoverActions({ ...coverActions, setPaymentType: { paymentType } })
    DataLayer.customEvent({
      eventName: `${paymentType.toLocaleLowerCase()} payment preference`,
      section: 'cover',
      eventType: 'form tracking',
    })
  }

  const handleRenewalQuote = (current: string) => {
    const currentToNumber = current ? parseInt(current) : 0
    setRenewalValue(currentToNumber)
    setCoverActions({ ...coverActions, setCurrentRenewalPrice: { current: currentToNumber } })
  }

  const handlePrefilledPaymentFrequency = (paymentFrequency: PaymentType | PremiumPaymentFrequency | undefined) => {
    if (paymentFrequency === PaymentType.RefundAtRenewalYearly) {
      return PaymentType.RefundAtRenewalYearly
    } else {
      return !paymentFrequency ? undefined : PaymentType.Monthly
    }
  }

  const handlePrefilledInceptionDate = (policyStart: string | undefined | null) => {
    if (policyStart && dayjs(policyStart).isBefore(dayjs(), 'day')) {
      setPolicyStart(null)
      return ''
    }
    if (policyStart) {
      return dayjs(policyStart).isToday()
        ? `${dayjs(policyStart).format('MMM D, YYYY')} (now)`
        : dayjs(policyStart).format('MMM D, YYYY')
    }
    return ''
  }

  const excessOptionsArray = quote ? getPossibleExcessOptions(quote) : []

  const possibleExcessOptions = excessOptionsArray.map(option => ({ value: option, label: `£${option}` }))

  return (
    <>
      <GenericSectionStyle>
        <div className="generic-wrapper">
          <div className="question-container">
            <div className="question-text-container">
              <H5>When would you like this policy to start?</H5>
            </div>
            <div className="input-info-container" id="policyStart">
              <Dropdown
                handleDropdownChange={option => {
                  handlePolicyStart(option.value)
                }}
                isMobile={false}
                options={daysWithinNextMonth()}
                placeholder="Select"
                preFilled={handlePrefilledInceptionDate(policyStart)}
                states={handleDropdownState(
                  policyStart,
                  showErrorBanner && !validInputs?.policyStart ? notAnsweredMessage : '',
                )}
                errorMessage={!validInputs?.policyStart && showErrorBanner ? notAnsweredMessage : ''}
                width={DropdownWidth.Large}
                className="dropdown-medium-with-icon dropdown"
                dataCy="inception-date-dropdown-list"
                ariaLiveOnFocus
              />
              <ToolTip text={policyStartModalText} className="tool-tip tip-top-small" />
            </div>
          </div>
          <div className="question-container" id="excess">
            <div className="question-text-container">
              <H5>How much voluntary excess would you like on your policy?</H5>
              <ParagraphBody className="question-info">This is on top of any compulsory excess.</ParagraphBody>
            </div>
            <div className="input-info-container">
              <Dropdown
                handleDropdownChange={option => handlePolicyExcess(option.value)}
                isMobile={isMobile}
                options={possibleExcessOptions}
                placeholder="Select"
                preFilled={(typeof voluntaryExcess === 'number' && `£${voluntaryExcess}`) || ''}
                states={handleDropdownState(
                  voluntaryExcess,
                  showErrorBanner && !validInputs?.excess ? notAnsweredMessage : '',
                )}
                errorMessage={!validInputs?.excess && showErrorBanner ? notAnsweredMessage : ''}
                width={DropdownWidth.Large}
                className="dropdown-small-with-icon dropdown-bellow"
                dataCy="voluntary-excess-dropdown-list"
                ariaLiveOnFocus
              />
              <ToolTip text={excessModalText} className="tool-tip tip-top-small" />
            </div>
          </div>
          {!quote?.pricing && (
            <div className="question-container">
              <div className="question-text-container">
                <H5>How would you like to pay for your car insurance this year?</H5>
              </div>
              <SegmentedControl
                options={[
                  { label: 'monthly', value: PaymentType.Monthly },
                  { label: 'yearly', value: PaymentType.RefundAtRenewalYearly },
                ]}
                name="payment-frequency"
                preFilledValue={handlePrefilledPaymentFrequency(paymentFrequency || undefined)}
                error={!validInputs?.paymentType && showErrorBanner ? notAnsweredMessage : ''}
                onClick={value => value && handlePaymentFrequency(value)}
                dataCy="payment-frequency-toggle"
                ariaLiveOnFocus
              />
            </div>
          )}
          <div className="question-container">
            <div className="question-text-container">
              <H5>How much is the renewal quote for your current car insurance?</H5>
              <ParagraphBody className="question-info">
                This is optional. It helps us keep our prices competitive.
              </ParagraphBody>
            </div>
            <div className="input-info-container">
              <Input
                placeHolder="Enter previous quote"
                name="renewal-quote"
                type="number"
                value={typeof renewalValue === 'number' ? renewalValue.toString() : ''}
                onChange={handleRenewalQuote}
                onBlur={() => {
                  DataLayer.customEvent({
                    eventName: 'renewal quote',
                    section: 'cover',
                    eventType: 'form tracking',
                  })
                }}
                isOptional={true}
                valid={typeof renewalValue === 'number'}
                prefix="£"
                dataCy="current-renewal-input-field"
                ariaLiveOnFocus
              />
              <ToolTip text={renewalQuoteModalText} className="tool-tip tip-top-small" />
            </div>
          </div>
        </div>
      </GenericSectionStyle>
    </>
  )
}
